import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import Layout from "../components/layoutBlack";
import {
  removeFromCart,
  setBoxData,
  setPriceData,
  setCheckoutAddresses,
  setShippingRates,
  clearShippingSelection,
} from "../store/slices/cartSlice.ts";
import { Formik, Form, Field, ErrorMessage, useFormikContext } from "formik";
import { config } from "../utils/constants";
import { US_STATES } from "../components/states.js";
import axios from "axios";
import * as Yup from "yup";
import countryData from "../components/countries.json";
import countryCodes from "../components/countryCodes.json";
import Select from "react-select";
import { showLoader } from "../store/slices/loader.ts";
import secureCheckoutImg from "../assets/images/icons/secure-checkout-png.png";

const getDialCode = (countryCode) => {
  if (!countryCode || !countryCodes[countryCode]) return "";
  return countryCodes[countryCode].dial_code || "";
};

const stripToDigits = (value = "") => String(value).replace(/\D/g, "");

const countDigits = (value = "") => (String(value).match(/\d/g) || []).length;

const formatPhoneForDisplay = (phone, country) => {
  const raw = String(phone || "").trim();
  if (!raw) return "";

  const dialCode = getDialCode(country);
  const dialDigits = stripToDigits(dialCode);
  const digits = stripToDigits(raw);

  if (!digits) return "";

  if (!dialCode) {
    return raw.startsWith("+") ? `+${digits}` : digits;
  }

  if (raw.startsWith("+")) {
    return `+${digits}`;
  }

  if (dialDigits && digits.startsWith(dialDigits)) {
    const national = digits.slice(dialDigits.length);
    return national ? `${dialCode} ${national}` : `${dialCode} `;
  }

  return `${dialCode} ${digits}`.trim();
};

const normalizePhoneForSubmit = (phone, country) => {
  const raw = String(phone || "").trim();
  if (!raw) return "";

  const dialCode = getDialCode(country);
  const dialDigits = stripToDigits(dialCode);
  const digits = stripToDigits(raw);

  if (!digits) return "";

  if (raw.startsWith("+")) return `+${digits}`;

  if (dialDigits && digits.startsWith(dialDigits)) {
    return `+${digits}`;
  }

  if (dialDigits) {
    return `+${dialDigits}${digits}`;
  }

  return digits;
};

const CountrySelect = ({ fieldName }) => {
  const { values, setFieldValue } = useFormikContext();

  const getCountryName = (label = "") =>
    label.replace(/^[^\p{L}\p{N}]+/gu, "").trim();

  const countries = React.useMemo(() => {
    const us = countryData.countries.find((c) => c.value === "US");

    const rest = countryData.countries
      .filter((c) => c.value !== "US")
      .slice()
      .sort((a, b) =>
        getCountryName(a.label).localeCompare(getCountryName(b.label), "en", {
          sensitivity: "base",
        })
      );

    return us ? [us, ...rest] : rest;
  }, []);

  const prefix = fieldName.split(".")[0];
  const selectedCountry = values?.[prefix]?.country || "";
  const currentPhone = values?.[prefix]?.phone_number || "";
  const value = countries.find((c) => c.value === selectedCountry) || null;

  const handleCountryChange = (option) => {
    const newCountry = option?.value || "";
    const newDialCode = getDialCode(newCountry);
    const previousDialCode = getDialCode(selectedCountry);
    const rawPhone = String(currentPhone || "").trim();

    setFieldValue(fieldName, newCountry, false);

    if (!rawPhone) {
      setFieldValue(
        `${prefix}.phone_number`,
        newDialCode ? `${newDialCode} ` : "",
        false
      );
      return;
    }

    const prevDialDigits = stripToDigits(previousDialCode);
    const newDialDigits = stripToDigits(newDialCode);
    const rawDigits = stripToDigits(rawPhone);

    let nationalDigits = rawDigits;

    if (prevDialDigits && rawDigits.startsWith(prevDialDigits)) {
      nationalDigits = rawDigits.slice(prevDialDigits.length);
    }

    const nextPhone = newDialDigits
      ? `${newDialCode} ${nationalDigits}`.trim()
      : nationalDigits;

    if (nextPhone !== currentPhone) {
      setFieldValue(`${prefix}.phone_number`, nextPhone, false);
    }
  };

  return (
    <Select
      name={fieldName}
      options={countries}
      value={value}
      onChange={handleCountryChange}
      classNamePrefix="react-select"
      className="react-select-container"
      placeholder="Select Country"
    />
  );
};

const StateField = ({ prefix }) => {
  const { values } = useFormikContext();
  const country = values[prefix].country;

  return country === "US" ? (
    <Field
      as="select"
      name={`${prefix}.state`}
      className="form-select"
      autoComplete="address-level1"
    >
      <option value="">Select State</option>
      {US_STATES.map(({ name, abbreviation }) => (
        <option key={abbreviation} value={abbreviation}>
          {name}
        </option>
      ))}
    </Field>
  ) : (
    <Field
      name={`${prefix}.state`}
      className="form-control"
      placeholder="State / Province / Region"
      autoComplete="address-level1"
    />
  );
};

const PhoneField = ({ prefix }) => {
  const { values, setFieldValue } = useFormikContext();
  const country = values[prefix].country;
  const isInternational = country && country !== "US";
  const fieldName = `${prefix}.phone_number`;
  const fieldValue = values[prefix].phone_number || "";
  const inputRef = useRef(null);
  const lastAutoFormattedRef = useRef("");

  const ensureDialCodeVisible = () => {
    const current = String(fieldValue || "").trim();
    if (!current) {
      const dialCode = getDialCode(country);
      if (dialCode) {
        setFieldValue(fieldName, `${dialCode} `, false);
      }
    }
  };

  const syncPhoneValue = (rawValue) => {
    const formatted = formatPhoneForDisplay(rawValue, country);
    if (formatted !== rawValue) {
      setFieldValue(fieldName, formatted, false);
    }
  };

  useEffect(() => {
    const raw = String(fieldValue || "").trim();
    if (!raw) {
      lastAutoFormattedRef.current = "";
      return;
    }

    const formatted = formatPhoneForDisplay(raw, country);

    if (
      formatted &&
      formatted !== fieldValue &&
      formatted !== lastAutoFormattedRef.current
    ) {
      lastAutoFormattedRef.current = formatted;
      setFieldValue(fieldName, formatted, false);
    } else if (formatted === fieldValue) {
      lastAutoFormattedRef.current = "";
    }
  }, [fieldValue, country, fieldName, setFieldValue]);

  return (
    <div className="mb-3 col-md-6">
      <label className="form-label">
        Phone Number {isInternational ? "*" : ""}
      </label>

      <Field name={fieldName}>
        {({ field }) => (
          <input
            {...field}
            ref={inputRef}
            type="tel"
            className="form-control"
            placeholder={isInternational ? "Enter phone number" : "Optional"}
            autoComplete="tel"
            onFocus={() => {
              ensureDialCodeVisible();
            }}
            onChange={(e) => {
              const newValue = e.target.value;
              if (newValue !== field.value) {
                setFieldValue(fieldName, newValue, false);
              }
            }}
            onBlur={(e) => {
              field.onBlur(e);
              syncPhoneValue(e.target.value);
            }}
          />
        )}
      </Field>

      <ErrorMessage
        name={fieldName}
        component="div"
        className="text-danger small"
      />
    </div>
  );
};

const SyncBillingFromShipping = ({ billingSameAsShipping }) => {
  const { values, setFieldValue } = useFormikContext();
  const lastSyncedShippingRef = useRef(null);

  useEffect(() => {
    if (!billingSameAsShipping) return;

    const shippingSnapshot = JSON.stringify(values.shipping);

    if (lastSyncedShippingRef.current !== shippingSnapshot) {
      setFieldValue("billing", { ...values.shipping }, false);
      lastSyncedShippingRef.current = shippingSnapshot;
    }
  }, [billingSameAsShipping, values.shipping, setFieldValue]);

  return null;
};

const makeAddressSchema = () =>
  Yup.object({
    firstName: Yup.string().required("Required"),
    lastName: Yup.string().required("Required"),
    email: Yup.string().email("Invalid email").required("Required"),
    street1: Yup.string().required("Required"),
    street2: Yup.string(),
    city: Yup.string().required("Required"),
    country: Yup.string().required("Required"),
    state: Yup.string().when("country", {
      is: "US",
      then: () => Yup.string().required("Required"),
      otherwise: () => Yup.string().notRequired(),
    }),
    zip: Yup.string().when("country", {
      is: "US",
      then: () => Yup.string().required("Required"),
      otherwise: () => Yup.string().notRequired(),
    }),
    phone_number: Yup.string().when("country", (country, schema) => {
      const resolvedCountry = Array.isArray(country) ? country[0] : country;
      const isInternational = !!resolvedCountry && resolvedCountry !== "US";

      return schema.test(
        "phone-valid-for-country",
        isInternational
          ? "Phone number is required for international shipping and must have at least 6 digits"
          : "Please enter a valid phone number",
        function (value) {
          const raw = String(value || "").trim();

          if (!raw) {
            return !isInternational;
          }

          const normalized = normalizePhoneForSubmit(raw, resolvedCountry);
          return countDigits(normalized) >= 6;
        }
      );
    }),
  });

const ShippingInfo = () => {
  const navigate = useNavigate();
  const REACT_APP_API_URL = config.url.REACT_APP_API_URL;
  const dispatch = useDispatch();
  const cart = useSelector((state) => state.cart);
  const items = useSelector((state) => state.cart.items);
  const profile = useSelector((state) => state.auth.account?.profile);
  const isWholesale = profile?.is_wholesale === true;

  const [billingSameAsShipping, setBillingSameAsShipping] = useState(true);
  const [discountCode, setDiscountCode] = useState("");
  const [discountAmount, setDiscountAmount] = useState(0);
  const [discountMessage, setDiscountMessage] = useState("");

  const subtotal = items.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );
  const discountValue = (subtotal * discountAmount).toFixed(2);
  const total = (subtotal - parseFloat(discountValue)).toFixed(2);

  const handleRemove = (id) => dispatch(removeFromCart(id));

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (values, { setSubmitting }) => {
    dispatch(showLoader("Finding carriers..."));

    const skus = items.flatMap((item) =>
      Array(Number(item.quantity) || 0).fill(item.sku)
    );

    const shipping = {
      ...values.shipping,
      phone_number: normalizePhoneForSubmit(
        values.shipping.phone_number,
        values.shipping.country
      ),
    };

    const billing = billingSameAsShipping
      ? { ...shipping }
      : {
          ...values.billing,
          phone_number: normalizePhoneForSubmit(
            values.billing.phone_number,
            values.billing.country
          ),
        };

    const priceData = {
      subtotal,
      total_price: parseFloat(total),
      discount_code: discountCode,
      discount_amount: discountAmount,
      discount_value: parseFloat(discountValue),
    };

    dispatch(setPriceData(priceData));

    dispatch(
      setCheckoutAddresses({
        shipping_info: shipping,
        billing_info: billing,
      })
    );

    dispatch(clearShippingSelection());

    try {
      const updatedCart = {
        ...cart,
        ...priceData,
      };

      const response = await axios.post(
        `${REACT_APP_API_URL}/get-shipping-info/`,
        {
          cart: updatedCart,
          skus,
          address: shipping,
        }
      );

      dispatch(
        setBoxData({
          box_length_inches: response.data.package_info["L"],
          box_width_inches: response.data.package_info["W"],
          box_height_inches: response.data.package_info["H"],
          box_weight_ounces: response.data.package_info["ounces"],
        })
      );

      dispatch(setShippingRates(response.data.data || {}));

      navigate("/choose-carrier");
    } catch (error) {
      console.error("Error submitting order:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const applyDiscount = async () => {
    try {
      const res = await axios.post(`${REACT_APP_API_URL}/check-discount/`, {
        code: discountCode,
      });

      const parsedDiscount = parseFloat(res.data.discount);
      const message = res.data.message;

      if (!isNaN(parsedDiscount)) {
        setDiscountAmount(parsedDiscount);
        setDiscountMessage(message);
      } else {
        setDiscountAmount(0);
        setDiscountMessage(message);
      }
    } catch (err) {
      setDiscountAmount(0);
      setDiscountMessage("");
    }
  };

  const validationSchema = Yup.object().shape({
    shipping: makeAddressSchema(),
    billing: billingSameAsShipping
      ? Yup.object().shape({})
      : makeAddressSchema(),
  });

  const initialValues = {
    shipping: {
      firstName: "",
      lastName: "",
      email: "",
      street1: "",
      street2: "",
      city: "",
      state: "",
      zip: "",
      country: "US",
      phone_number: "",
    },
    billing: {
      firstName: "",
      lastName: "",
      email: "",
      street1: "",
      street2: "",
      city: "",
      state: "",
      zip: "",
      country: "US",
      phone_number: "",
    },
  };

  return (
    <Layout>
      <section className="full-page-section bg0 p-t-60 p-b-80">
        <div className="container">
          <div className="row isotope-grid">
            <section className="bg0 p-t-62 p-b-60">
              <div className="container">
                <Formik
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                >
                  {() => (
                    <>
                      <SyncBillingFromShipping
                        billingSameAsShipping={billingSameAsShipping}
                      />

                      <Form className="row">
                        <div className="col-lg-5 mb-5">
                          <h4>Shopping Cart</h4>

                          <table className="table">
                            <thead className="table-light">
                              <tr>
                                <th>Product</th>
                                <th>Qty</th>
                                <th>Total</th>
                                <th></th>
                              </tr>
                            </thead>
                            <tbody>
                              {items.map((item) => (
                                <tr key={item.id}>
                                  <td>{item.name}</td>
                                  <td>{item.quantity}</td>
                                  <td>${(item.price * item.quantity).toFixed(2)}</td>
                                  <td>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-danger"
                                      onClick={() => handleRemove(item.id)}
                                    >
                                      Remove
                                    </button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>

                          {!isWholesale && (
                            <div className="mt-3">
                              {discountMessage && (
                                <div
                                  className={`alert ${
                                    discountAmount > 0
                                      ? "alert-success"
                                      : "alert-danger"
                                  }`}
                                  role="alert"
                                >
                                  {discountMessage}
                                </div>
                              )}

                              <label className="form-label">Discount Code</label>
                              <div className="input-group">
                                <input
                                  type="text"
                                  className="form-control"
                                  value={discountCode}
                                  onChange={(e) => setDiscountCode(e.target.value)}
                                  placeholder="Enter discount code"
                                />
                                <button
                                  type="button"
                                  className="btn btn-outline-secondary"
                                  onClick={applyDiscount}
                                >
                                  Apply
                                </button>
                              </div>
                            </div>
                          )}

                          <div className="card-footer d-flex justify-content-between py-3">
                            <div>
                              <div>
                                <strong>Subtotal:</strong>
                              </div>
                              {discountAmount > 0 && (
                                <div>
                                  <strong>Discount:</strong>
                                </div>
                              )}
                              <div>
                                <strong>Total:</strong>
                              </div>
                            </div>
                            <div className="text-end">
                              <div>${subtotal.toFixed(2)}</div>
                              {discountAmount > 0 && (
                                <div className="text-success">– ${discountValue}</div>
                              )}
                              <div>
                                <strong>${total}</strong>
                              </div>
                            </div>
                          </div>

                          <div className="col-9 d-none d-lg-block mt-4">
                            <button type="submit" className="btn btn-primary w-100">
                              Continue to Payment
                            </button>

                            <div
                              className="mt-3 w-100 d-flex justify-content-center p-t-20"
                              style={{ overflow: "hidden" }}
                            >
                              <img
                                src={secureCheckoutImg}
                                alt="Guaranteed safe checkout"
                                style={{
                                  display: "block",
                                  width: "80%",
                                  maxWidth: "80%",
                                  height: "auto",
                                }}
                              />
                            </div>
                          </div>
                        </div>

                        <div className="col-lg-7">
                          <div className="card shadow-sm">
                            <div className="card-body">
                              <h4>Shipping Address</h4>
                              <div className="row">
                                {[
                                  "firstName",
                                  "lastName",
                                  "email",
                                  "street1",
                                  "street2",
                                  "city",
                                  "zip",
                                ].map((field) => (
                                  <div
                                    className="mb-3 col-md-6"
                                    key={`shipping.${field}`}
                                  >
                                    <label className="form-label">
                                      {field.replace(/([A-Z])/g, " $1")}
                                    </label>
                                    <Field
                                      name={`shipping.${field}`}
                                      className="form-control"
                                      autoComplete={
                                        field === "firstName"
                                          ? "given-name"
                                          : field === "lastName"
                                          ? "family-name"
                                          : field === "email"
                                          ? "email"
                                          : field === "street1"
                                          ? "address-line1"
                                          : field === "street2"
                                          ? "address-line2"
                                          : field === "city"
                                          ? "address-level2"
                                          : field === "zip"
                                          ? "postal-code"
                                          : "off"
                                      }
                                    />
                                    <ErrorMessage
                                      name={`shipping.${field}`}
                                      component="div"
                                      className="text-danger small"
                                    />
                                  </div>
                                ))}

                                <PhoneField prefix="shipping" />

                                <div className="mb-3 col-md-6">
                                  <label className="form-label">Country</label>
                                  <CountrySelect fieldName="shipping.country" />
                                  <ErrorMessage
                                    name="shipping.country"
                                    component="div"
                                    className="text-danger small"
                                  />
                                </div>

                                <div className="mb-3 col-md-6">
                                  <label className="form-label">State</label>
                                  <StateField prefix="shipping" />
                                  <ErrorMessage
                                    name="shipping.state"
                                    component="div"
                                    className="text-danger small"
                                  />
                                </div>
                              </div>

                              <div className="form-check form-switch my-3">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={billingSameAsShipping}
                                  onChange={() =>
                                    setBillingSameAsShipping(!billingSameAsShipping)
                                  }
                                  id="billingSwitch"
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor="billingSwitch"
                                >
                                  Billing same as shipping address
                                </label>
                              </div>

                              {!billingSameAsShipping && (
                                <>
                                  <h4>Billing Address</h4>
                                  <div className="row">
                                    {[
                                      "firstName",
                                      "lastName",
                                      "email",
                                      "street1",
                                      "street2",
                                      "city",
                                      "zip",
                                    ].map((field) => (
                                      <div
                                        className="mb-3 col-md-6"
                                        key={`billing.${field}`}
                                      >
                                        <label className="form-label">
                                          {field.replace(/([A-Z])/g, " $1")}
                                        </label>
                                        <Field
                                          name={`billing.${field}`}
                                          className="form-control"
                                          autoComplete={
                                            field === "firstName"
                                              ? "billing given-name"
                                              : field === "lastName"
                                              ? "billing family-name"
                                              : field === "email"
                                              ? "billing email"
                                              : field === "street1"
                                              ? "billing address-line1"
                                              : field === "street2"
                                              ? "billing address-line2"
                                              : field === "city"
                                              ? "billing address-level2"
                                              : field === "zip"
                                              ? "billing postal-code"
                                              : "off"
                                          }
                                        />
                                        <ErrorMessage
                                          name={`billing.${field}`}
                                          component="div"
                                          className="text-danger small"
                                        />
                                      </div>
                                    ))}

                                    <PhoneField prefix="billing" />

                                    <div className="mb-3 col-md-6">
                                      <label className="form-label">Country</label>
                                      <CountrySelect fieldName="billing.country" />
                                      <ErrorMessage
                                        name="billing.country"
                                        component="div"
                                        className="text-danger small"
                                      />
                                    </div>

                                    <div className="mb-3 col-md-6">
                                      <label className="form-label">State</label>
                                      <StateField prefix="billing" />
                                      <ErrorMessage
                                        name="billing.state"
                                        component="div"
                                        className="text-danger small"
                                      />
                                    </div>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>

                          <div className="col-12 mt-auto d-lg-none">
                            <button
                              type="submit"
                              className="btn btn-primary w-100 mt-5"
                            >
                              Continue to Payment
                            </button>

                            <div
                              className="mt-3 w-100 d-flex justify-content-center p-t-20"
                              style={{ overflow: "hidden" }}
                            >
                              <img
                                src={secureCheckoutImg}
                                alt="Guaranteed safe checkout"
                                style={{
                                  display: "block",
                                  width: "80%",
                                  maxWidth: "80%",
                                  height: "auto",
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      </Form>
                    </>
                  )}
                </Formik>
              </div>
            </section>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ShippingInfo;